import { CheckCircle2, Lightbulb } from 'lucide-react';

import type { AnswerCheck, EnergyDirection } from './energyCalorimetry';
import { formatSig, formatSigned } from './energyCalorimetry';
import type { EnergyProfile } from './energyCalorimetrySenior';

const THERMOMETER_MIN_C = 0;
const THERMOMETER_MAX_C = 50;
const THERMOMETER_TICK_COUNT = 5;

function thermometerFraction(temperatureC: number, maxC: number): number {
  const clamped = Math.min(maxC, Math.max(THERMOMETER_MIN_C, temperatureC));
  return (clamped - THERMOMETER_MIN_C) / (maxC - THERMOMETER_MIN_C);
}

/** A thermometer column drawn from the model reading (0–50 °C scale unless `maxC` is given). */
export function ThermometerReading({
  label,
  temperatureC,
  tone,
  maxC = THERMOMETER_MAX_C,
}: {
  label: string;
  temperatureC: number;
  tone: 'start' | 'warmer' | 'cooler';
  maxC?: number;
}) {
  const fill =
    tone === 'warmer' ? 'bg-brand-coral' : tone === 'cooler' ? 'bg-brand-sky' : 'bg-slate2';
  const ticks = Array.from(
    { length: THERMOMETER_TICK_COUNT + 1 },
    (_, index) => THERMOMETER_MIN_C + (index * (maxC - THERMOMETER_MIN_C)) / THERMOMETER_TICK_COUNT,
  );
  return (
    <div className="flex flex-col items-center">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">{label}</p>
      <div
        className="mt-3 flex items-end gap-2"
        role="img"
        aria-label={`${label}: thermometer reads ${temperatureC.toFixed(1)} degrees Celsius`}
      >
        <div className="relative h-48 w-5 overflow-hidden rounded-full border-2 border-hairline bg-white">
          <div
            className={`absolute inset-x-0 bottom-0 rounded-full transition-[height] duration-700 motion-reduce:transition-none ${fill}`}
            style={{ height: `${thermometerFraction(temperatureC, maxC) * 100}%` }}
          />
        </div>
        <div className="relative h-48 w-8 text-[10px] font-bold text-slate2" aria-hidden="true">
          {ticks.map((tick) => (
            <span
              key={tick}
              className="absolute left-0 whitespace-nowrap"
              style={{ bottom: `${thermometerFraction(tick, maxC) * 100}%`, transform: 'translateY(50%)' }}
            >
              – {tick}
            </span>
          ))}
        </div>
      </div>
      <p className="mt-3 text-lg font-extrabold text-ink">{temperatureC.toFixed(1)} °C</p>
    </div>
  );
}

/** Not-to-scale energy-level diagram: reactants vs products. */
export function EnergyLevelDiagram({
  direction,
  deltaHText,
}: {
  direction: EnergyDirection;
  /** Optional value from the learner's own calculation, added to the arrow label. */
  deltaHText?: string;
}) {
  const exo = direction === 'exothermic';
  const description = exo
    ? 'Energy-level diagram: products are lower than reactants; energy is released to the surroundings'
    : 'Energy-level diagram: products are higher than reactants; energy is taken in from the surroundings';
  return (
    <div
      className="rounded-2xl border border-hairline bg-surface p-4"
      role="img"
      aria-label={deltaHText ? `${description}; ${deltaHText}` : description}
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">
        Energy level (not to scale)
      </p>
      <div className="relative mt-3 h-36" aria-hidden="true">
        <div className="absolute bottom-0 left-0 top-0 border-l-2 border-ink/40" />
        <span className="absolute -left-1 top-0 text-[10px] font-bold text-slate2">▲ energy</span>
        <div
          className="absolute left-6 w-[38%] border-t-4 border-ink"
          style={{ top: exo ? '28%' : '72%' }}
        >
          <span className="mt-1 block text-xs font-extrabold text-ink">Reactants</span>
        </div>
        <div
          className={`absolute right-2 w-[38%] border-t-4 ${exo ? 'border-brand-coral' : 'border-brand-sky'}`}
          style={{ top: exo ? '72%' : '28%' }}
        >
          <span className="mt-1 block text-xs font-extrabold text-ink">Products</span>
        </div>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white px-3 py-1 text-xs font-extrabold text-ink">
          {exo ? '↓ energy released' : '↑ energy taken in'}
        </span>
      </div>
      {deltaHText ? (
        <p className="mt-2 text-center font-mono text-xs font-extrabold text-ink" aria-hidden="true">
          {deltaHText}
        </p>
      ) : null}
    </div>
  );
}

const PROFILE_WIDTH = 400;
const PROFILE_HEIGHT = 250;
const PROFILE_TOP = 30;
const PROFILE_BOTTOM = 210;
const X_REACTANT_END = 110;
const X_PEAK = 200;
const X_PRODUCT_START = 290;
const X_LEFT = 40;
const X_RIGHT = 360;

/** Energy profile drawn from ΔH and an illustrative Ea; optional catalysed pathway. */
export function EnergyProfileChart({
  profile,
  showCatalyst,
}: {
  profile: EnergyProfile;
  showCatalyst: boolean;
}) {
  const highest = profile.peakLevel;
  const lowest = Math.min(profile.reactantsLevel, profile.productsLevel);
  const y = (energy: number) =>
    PROFILE_TOP + ((highest - energy) / (highest - lowest)) * (PROFILE_BOTTOM - PROFILE_TOP);
  const reactantY = y(profile.reactantsLevel);
  const productY = y(profile.productsLevel);
  const path = (peak: number) => {
    const peakY = y(peak);
    return `M${X_LEFT} ${reactantY} L${X_REACTANT_END} ${reactantY} C${X_REACTANT_END + 45} ${reactantY} ${X_PEAK - 45} ${peakY} ${X_PEAK} ${peakY} C${X_PEAK + 45} ${peakY} ${X_PRODUCT_START - 45} ${productY} ${X_PRODUCT_START} ${productY} L${X_RIGHT} ${productY}`;
  };
  const deltaHx = X_RIGHT - 20;
  const label = `Energy profile: reactants at 0 kJ, peak at ${formatSig(profile.forwardEa, 3)} kJ, products at ${formatSigned(profile.deltaH, 3)} kJ${showCatalyst ? `, catalysed peak at ${formatSig(profile.catalysedEa, 3)} kJ` : ''}`;

  return (
    <svg
      viewBox={`0 0 ${PROFILE_WIDTH} ${PROFILE_HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label={label}
    >
      <line x1="24" y1="20" x2="24" y2={PROFILE_BOTTOM + 15} className="stroke-ink/40" strokeWidth="2" />
      <line
        x1="24"
        y1={PROFILE_BOTTOM + 15}
        x2={X_RIGHT + 20}
        y2={PROFILE_BOTTOM + 15}
        className="stroke-ink/40"
        strokeWidth="2"
      />
      <text x="28" y="16" className="fill-slate2 text-[11px] font-bold">
        Enthalpy, H
      </text>
      <text x={X_RIGHT - 70} y={PROFILE_BOTTOM + 32} className="fill-slate2 text-[11px] font-bold">
        Reaction progress →
      </text>
      <path d={path(profile.peakLevel)} fill="none" className="stroke-ink" strokeWidth="3" />
      {showCatalyst ? (
        <path
          d={path(profile.catalysedPeakLevel)}
          fill="none"
          className="stroke-brand-mint"
          strokeWidth="3"
          strokeDasharray="7 5"
        />
      ) : null}
      <line
        x1={X_PEAK}
        y1={reactantY}
        x2={X_PEAK}
        y2={y(profile.peakLevel)}
        className="stroke-brand-coral"
        strokeWidth="2"
        markerEnd="url(#ea-arrow)"
      />
      <line x1={X_REACTANT_END} y1={reactantY} x2={X_PEAK + 30} y2={reactantY} className="stroke-ink/30" strokeDasharray="4 4" />
      <text x={X_PEAK + 6} y={(reactantY + y(profile.peakLevel)) / 2} className="fill-brand-coral text-[12px] font-extrabold">
        Ea {formatSig(profile.forwardEa, 3)} kJ
      </text>
      <line
        x1={deltaHx}
        y1={reactantY}
        x2={deltaHx}
        y2={productY}
        className={profile.direction === 'exothermic' ? 'stroke-brand-coral' : 'stroke-brand-sky'}
        strokeWidth="2"
        markerEnd="url(#dh-arrow)"
      />
      <line x1={X_PRODUCT_START} y1={reactantY} x2={X_RIGHT} y2={reactantY} className="stroke-ink/30" strokeDasharray="4 4" />
      <text x={deltaHx - 6} y={(reactantY + productY) / 2} textAnchor="end" className="fill-ink text-[12px] font-extrabold">
        ΔH {formatSigned(profile.deltaH, 3)} kJ
      </text>
      <text x={X_LEFT} y={reactantY - 8} className="fill-ink text-[11px] font-bold">
        Reactants
      </text>
      <text x={X_PRODUCT_START} y={productY - 8} className="fill-ink text-[11px] font-bold">
        Products
      </text>
      {showCatalyst ? (
        <text x={X_PEAK - 30} y={y(profile.catalysedPeakLevel) + 18} className="fill-brand-mint text-[11px] font-extrabold">
          catalysed
        </text>
      ) : null}
      <defs>
        <marker id="ea-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" className="fill-brand-coral" />
        </marker>
        <marker id="dh-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" className="fill-ink" />
        </marker>
      </defs>
    </svg>
  );
}

/** Horizontal bars generated from values; colour is paired with the printed value. */
export function ValueBars({
  rows,
  unit,
  highlightId,
}: {
  rows: { id: string; label: string; value: number }[];
  unit: string;
  highlightId?: string;
}) {
  const maximum = Math.max(...rows.map((row) => row.value));
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.id} className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-3 text-sm">
          <span className={`font-extrabold ${row.id === highlightId ? 'text-brand-coral' : 'text-ink'}`}>
            {row.label}
            {row.id === highlightId ? ' ★' : ''}
          </span>
          <span className="flex min-w-0 items-center gap-2">
            <span
              className={`h-4 rounded-full ${row.id === highlightId ? 'bg-brand-coral' : 'bg-brand-sky'}`}
              style={{ width: `${Math.max(2, (row.value / maximum) * 70)}%` }}
              aria-hidden="true"
            />
            <span className="whitespace-nowrap text-xs font-bold text-slate2">
              {formatSig(row.value, 3)} {unit}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function FeedbackCard({ title, check }: { title: string; check: AnswerCheck }) {
  const correct = check.verdict === 'correct';
  return (
    <div
      role="status"
      className={`rounded-2xl border p-4 ${correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
    >
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        {correct ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-mint" aria-hidden="true" />
        ) : (
          <Lightbulb className="h-5 w-5 shrink-0 text-brand-coral" aria-hidden="true" />
        )}
        {title}: {correct ? 'correct' : 'not yet'}
      </p>
      <p className="mt-1 text-sm font-medium leading-6 text-slate2">{check.feedback}</p>
    </div>
  );
}
